import React, { useState, useEffect } from 'react';
import { auth, updatePassword, reauthenticateWithCredential, EmailAuthProvider, getUserData, db } from '../firebase';
import { onSnapshot, doc, updateDoc } from 'firebase/firestore';
import VIPPricing from './VIPPricing';
import VIPFeatures from './VIPFeatures';
import './ProfilePage.css';

const ProfilePage = ({ onClose }) => {
  const user = auth.currentUser;
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showVIPPricing, setShowVIPPricing] = useState(false);
  const [showVIPFeatures, setShowVIPFeatures] = useState(false);

  const updateToRegularRole = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        role: 'regular'
      });
      console.log('User role updated to regular due to expiration');
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  useEffect(() => {
    if (user) {
      // Set up real-time listener for user data
      const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setUserData(data);

          // Check if VIP account has expired
          if (data.role === 'vip' && data.expirationDate) {
            const expDate = data.expirationDate.toDate();
            const now = new Date();
            if (expDate < now) {
              updateToRegularRole(user.uid);
            }
          }
        }
      });
      
      // Cleanup subscription on unmount
      return () => unsubscribe();
    }
  }, [user]);

  const calculateRemainingDays = (expirationDate) => {
    if (!expirationDate) return 0;
    const expDate = expirationDate.toDate();
    const now = new Date();
    const diffTime = expDate - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate();
    return d.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (newPassword) {
        if (newPassword !== confirmPassword) {
          throw new Error('Mật khẩu mới không khớp');
        }

        if (newPassword.length < 6) {
          throw new Error('Mật khẩu mới phải có ít nhất 6 ký tự');
        }

        // Re-authenticate user
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);

        // Update password
        await updatePassword(user, newPassword);
        setMessage('Cập nhật mật khẩu thành công');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
      }
    } catch (err) {
      let errorMessage = 'Đã có lỗi xảy ra. Vui lòng thử lại.';
      if (err.code === 'auth/wrong-password') {
        errorMessage = 'Mật khẩu hiện tại không đúng';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Mật khẩu mới quá yếu';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleShowVIPPricing = () => {
    setShowVIPPricing(true);
    setShowVIPFeatures(false);
  };

  const handleCloseVIPPricing = () => {
    setShowVIPPricing(false);
  };

  const handleShowVIPFeatures = () => {
    setShowVIPFeatures(true);
    setShowVIPPricing(false);
  };

  const handleCloseVIPFeatures = () => {
    setShowVIPFeatures(false);
  };

  const getBadgeText = (role) => {
    switch(role) {
      case 'pioneer':
        return 'PIONEER';
      case 'admin':
        return 'ADMIN';
      case 'vip':
        return 'VIP';
      default:
        return '';
    }
  }

  return (
    <>
      <div className="profile-modal-overlay">
        <div className="profile-modal">
          <button className="profile-modal-close" onClick={onClose}>✕</button>
          
          <div className="profile-header">
            <div className="profile-name-container">
              <h3 className="profile-name">{user?.displayName || 'Người dùng'}</h3>
              {userData?.role && (
                <div className={`user-badge ${userData.role}`}>
                  {userData.role === 'pioneer' && <i className="fas fa-rocket"></i>}
                  {userData.role === 'admin' && <i className="fas fa-shield-alt"></i>}
                  {userData.role === 'vip' && <i className="fas fa-crown"></i>}
                  <span>
                    {userData.role === 'pioneer' ? 'PIONEER' :
                     userData.role === 'admin' ? 'ADMIN' :
                     userData.role === 'vip' ? 'VIP' :
                     'FREE'}
                  </span>
                </div>
              )}
            </div>
            <p className="profile-email">{user?.email}</p>
            <div className="profile-role-container">
              <div className="profile-role">
                {userData?.role === 'admin' ? (
                  <>
                    <i className="fas fa-shield-alt" style={{ 
                      color: '#fff',
                      marginRight: '5px',
                      textShadow: '0 0 10px rgba(255,255,255,0.5)',
                      animation: 'glowAdmin 2s ease-in-out infinite alternate'
                    }}></i>
                    <span style={{ 
                      background: 'linear-gradient(45deg, #FF3D3D, #CC0000, #990000)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      fontWeight: 'bold',
                      textShadow: '0 0 10px rgba(255,61,61,0.3)',
                      animation: 'gradientShift 3s ease infinite'
                    }}>Admin</span>
                  </>
                ) : userData?.role === 'pioneer' ? (
                  <>
                    <i className="fas fa-rocket" style={{ 
                      color: '#fff',
                      marginRight: '5px',
                      textShadow: '0 0 10px rgba(255,255,255,0.5)',
                      animation: 'glow 2s ease-in-out infinite alternate'
                    }}></i>
                    <span style={{ 
                      background: 'linear-gradient(45deg, #FF4081, #9C27B0, #673AB7)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      fontWeight: 'bold',
                      textShadow: '0 0 10px rgba(156,39,176,0.3)',
                      animation: 'gradientShift 3s ease infinite'
                    }}>Người Tiên Phong</span>
                  </>
                ) : userData?.role === 'vip' ? (
                  <>
                    <i className="fas fa-crown" style={{ 
                      color: '#fff',
                      marginRight: '5px',
                      textShadow: '0 0 10px rgba(255,255,255,0.5)',
                      animation: 'glowVIP 2s ease-in-out infinite alternate'
                    }}></i>
                    <span style={{ 
                      background: 'linear-gradient(45deg, #FFD700, #FFA500, #FF8C00)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      fontWeight: 'bold',
                      textShadow: '0 0 10px rgba(255,215,0,0.3)',
                      animation: 'gradientShift 3s ease infinite'
                    }}>Tài khoản VIP</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-user" style={{ color: '#666666', marginRight: '5px' }}></i>
                    <span style={{ color: '#666666' }}>Tài khoản thường</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="profile-stats">
            <div className="profile-stat">
              <div className="profile-stat-value">
                {userData?.analysisCount || 0}
              </div>
              <div className="profile-stat-label">
                Lượt phân tích đã dùng
              </div>
            </div>
            <div className="profile-stat">
              <div className="profile-stat-value">
                {userData?.maxAnalysisCount || 9}
              </div>
              <div className="profile-stat-label">
                Tổng lượt được phép
              </div>
            </div>
          </div>

          {/* Add notification when analysis count reaches limit */}
          {userData?.analysisCount >= userData?.maxAnalysisCount && (
            <div className="analysis-limit-notice">
              <div className="limit-warning">
                <i className="fas fa-exclamation-circle" style={{ color: '#ff6b6b', marginRight: '8px' }}></i>
                Tài khoản của bạn đã hết hạn/lượt
              </div>
              <button 
                className="upgrade-vip-button"
                onClick={handleShowVIPPricing}
              >
                👑 <strong>Nâng cấp VIP ngay</strong>
              </button>
            </div>
          )}

          <div className="profile-expiration">
            {userData?.expirationDate && (
              <div className="expiration-info">
                {calculateRemainingDays(userData.expirationDate) > 0 ? (
                  <>
                    <i className="fas fa-clock" style={{ marginRight: '5px', color: '#666666' }}></i>
                    <div className="expiration-details">
                      <div>
                        <span>Thời hạn sử dụng còn: </span>
                        <span className="expiration-date">
                          {calculateRemainingDays(userData.expirationDate)} ngày
                        </span>
                      </div>
                      <div className="expiration-full-date">
                        (Hết hạn ngày: {formatDate(userData.expirationDate)})
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="expired-notice">
                    <div className="expired-text-container">
                      <i className="fas fa-exclamation-circle" style={{ color: '#e74c3c' }}></i>
                      <span>Tài khoản của bạn đã hết hạn/lượt</span>
                    </div>
                    <button 
                      className="upgrade-vip-button"
                      onClick={handleShowVIPPricing}
                    >
                      👑 <strong>Nâng cấp VIP ngay</strong>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <hr className="profile-divider" />

          <div className="password-section">
            <h3>Đổi mật khẩu</h3>
            <form onSubmit={handleUpdateProfile}>
              <div className="profile-input-group">
                <label htmlFor="currentPassword">Mật khẩu hiện tại</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Nhập mật khẩu hiện tại"
                  required={!!newPassword}
                />
              </div>

              <div className="profile-input-group">
                <label htmlFor="newPassword">Mật khẩu mới</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nhập mật khẩu mới"
                />
              </div>

              <div className="profile-input-group">
                <label htmlFor="confirmPassword">Xác nhận mật khẩu mới</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Nhập lại mật khẩu mới"
                />
              </div>

              {error && <div className="profile-error">{error}</div>}
              {message && <div className="profile-message">{message}</div>}

              <button 
                type="submit" 
                disabled={loading || (!currentPassword && !newPassword)} 
                className="profile-button"
              >
                {loading ? (
                  <>
                    Đang cập nhật...
                    <div className="loading-spinner"></div>
                  </>
                ) : (
                  'Cập nhật mật khẩu'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {showVIPPricing && (
        <VIPPricing 
          onClose={handleCloseVIPPricing}
          onShowFeatures={handleShowVIPFeatures}
        />
      )}

      {showVIPFeatures && (
        <VIPFeatures 
          onClose={handleCloseVIPFeatures}
          onShowPricing={handleShowVIPPricing}
        />
      )}
    </>
  );
};

export default ProfilePage;
